import React, { useMemo } from "react";
import { Box } from "@material-ui/core";
import { BillDetails, BillType } from "../../../api/services";
import { formatSpacedStringSentenceCase } from "../../../utils/formatStringSpacing";
import { formatBillPeriod } from "../utils/formatBillPeriod";
import { formatDate } from "../../../utils/formatDate";
import { FileAction } from "./FileAction";
import { BillRowChips } from "./BillRowChips";
import {
  CustomTableRow,
  SingleLineTableCell,
} from "../../../components/CustomTableComponents";
import { BillDetailsLink } from "./DetailsLink";
import { formatMoneyWithCR } from "../../../utils/formatMoney";

const formatBillType = (type: BillType) => {
  switch (type) {
    case "Hot":
      return "Hot Bill";
    case "NewVersion":
      return "New Version";
    case "Manual":
      return "Manual Bill";
    case "CreditNote":
      return "Credit Note";
    default:
      return type;
  }
};

export const BillRow: React.FC<{
  bill: BillDetails;
}> = ({ bill }) => {
  const {
    issueDt,
    dueDt,
    status,
    type: billType,
    fromDttm,
    toDttm,
    currency,
    grossAmount,
    billProps,
    number,
    files,
  } = bill;

  const { isSuperseded } = billProps || {};

  const {
    date,
    type,
    period,
    amount,
    due,
    isGreyedOut,
    pdfFile,
  } = useMemo(() => {
    const pdfFiles = files
      .filter((a) => a.fileType === "pdf")
      .sort((a, b) => a.orderNo.localeCompare(b.orderNo));

    return {
      date: formatDate(issueDt),
      type: formatBillType(billType),
      period: formatBillPeriod(fromDttm, toDttm),
      amount: `${formatMoneyWithCR(currency, grossAmount, {
        transform: (n) => (billType === "CreditNote" ? -1 : 1) * n,
      })}`,
      due: formatDate(dueDt),
      isGreyedOut:
        (status !== "Accepted" && status !== "Authorised") || !!isSuperseded,
      pdfFile: pdfFiles.find((a) => a.status === "Ready") || pdfFiles[0],
    };
  }, [
    billType,
    currency,
    dueDt,
    files,
    fromDttm,
    grossAmount,
    isSuperseded,
    issueDt,
    status,
    toDttm,
  ]);

  return (
    <CustomTableRow
      isStruck={!!isSuperseded}
      isGrey={isGreyedOut}
      height="50px"
    >
      <SingleLineTableCell data-testid="BillRow__TableCell--Date">
        {date}
      </SingleLineTableCell>
      <SingleLineTableCell data-testid="BillRow__TableCell--Type">
        {type}
      </SingleLineTableCell>
      <SingleLineTableCell data-testid="BillRow__TableCell--Period">
        {period}
      </SingleLineTableCell>
      <SingleLineTableCell data-testid="BillRow__TableCell--Status">
        {formatSpacedStringSentenceCase(status)}
      </SingleLineTableCell>
      <SingleLineTableCell data-testid="BillRow__TableCell--Amount">
        <Box textAlign="right">{amount}</Box>
      </SingleLineTableCell>
      <SingleLineTableCell data-testid="BillRow__TableCell--Due">
        {due}
      </SingleLineTableCell>
      <SingleLineTableCell data-testid="BillRow__TableCell--Actions">
        {/* set inline-block so it wont be struck through */}
        <Box display="inline-block" width="100%">
          <Box display="flex" alignItems="center">
            <FileAction file={pdfFile} />
            <BillDetailsLink billType={billType} number={number} />
            <Box
              display="flex"
              flexDirection="row"
              flexWrap="nowrap"
              alignItems="center"
              ml={2.5}
              flexGrow={1}
            >
              {billProps ? <BillRowChips {...billProps} /> : null}
            </Box>
          </Box>
        </Box>
      </SingleLineTableCell>
    </CustomTableRow>
  );
};
